import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, CalendarDays, Plus, Trash2, Loader2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const HolidayManagement = () => {
    const navigate = useNavigate();
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [formData, setFormData] = useState({
        name: '',
        date: '',
        description: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

    useEffect(() => {
        fetchHolidays();
    }, [selectedYear]);

    const fetchHolidays = async () => {
        try {
            setLoading(true);
            const response = await api.getHolidays(selectedYear);
            setHolidays(response.items || []);
        } catch (error) {
            console.error('Error fetching holidays:', error);
            toast({
                title: 'Error',
                description: 'Failed to load holidays',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.date) {
            toast({
                title: 'Validation Error',
                description: 'Name and Date are required',
                variant: 'destructive'
            });
            return;
        }

        try {
            setSubmitting(true);
            await api.createHoliday({
                name: formData.name.trim(),
                date: formData.date,
                description: formData.description.trim() || null
            });
            toast({
                title: 'Success',
                description: 'Holiday added successfully'
            });
            setShowAddModal(false);
            setFormData({ name: '', date: '', description: '' });
            fetchHolidays();
        } catch (error) {
            console.error('Error creating holiday:', error);
            toast({
                title: 'Error',
                description: error.message || 'Failed to add holiday',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

        try {
            setDeletingId(id);
            await api.deleteHoliday(id);
            toast({
                title: 'Success',
                description: 'Holiday deleted successfully'
            });
            setHolidays(prev => prev.filter(h => h.id !== id));
        } catch (error) {
            console.error('Error deleting holiday:', error);
            toast({
                title: 'Error',
                description: 'Failed to delete holiday',
                variant: 'destructive'
            });
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Group holidays by month
    const groupedByMonth = holidays.reduce((acc, holiday) => {
        const month = new Date(holiday.date).toLocaleString('en-IN', { month: 'long' });
        if (!acc[month]) acc[month] = [];
        acc[month].push(holiday);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="text-gray-600">
                                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Settings
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shrink-0">
                                <CalendarDays className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Holiday Management</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Manage holidays for the year</p>
                            </div>
                        </div>
                        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                            {/* Year Selector */}
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className="border rounded-lg px-3 py-2 text-sm bg-white"
                            >
                                {yearOptions.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                            <Button
                                className="bg-teal-600 hover:bg-teal-700"
                                onClick={() => {
                                    setFormData({ name: '', date: '', description: '' });
                                    setShowAddModal(true);
                                }}
                            >
                                <Plus className="w-4 h-4 mr-2" /> Add Holiday
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Holiday Count */}
                <div className="mb-4 text-sm text-gray-600">
                    Total: <span className="font-semibold">{holidays.length}</span> holiday{holidays.length !== 1 ? 's' : ''} in {selectedYear}
                </div>

                {/* Holiday List */}
                {loading ? (
                    <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-teal-600" />
                        <p className="text-gray-500 mt-2">Loading holidays...</p>
                    </div>
                ) : holidays.length === 0 ? (
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-center py-12">
                                <CalendarDays className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                <p className="text-gray-500 text-lg">No holidays added for {selectedYear}</p>
                                <p className="text-gray-400 text-sm mt-1">Click "Add Holiday" to get started</p>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(groupedByMonth).map(([month, monthHolidays]) => (
                            <Card key={month}>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg text-gray-700">{month}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="divide-y">
                                        {monthHolidays.map((holiday) => (
                                            <div key={holiday.id} className="flex items-center justify-between py-3">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                                                        <span className="text-red-600 font-bold text-sm">
                                                            {new Date(holiday.date).getDate()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{holiday.name}</p>
                                                        <p className="text-sm text-gray-500">{formatDate(holiday.date)}</p>
                                                        {holiday.description && (
                                                            <p className="text-xs text-gray-400 mt-0.5">{holiday.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(holiday.id, holiday.name)}
                                                    disabled={deletingId === holiday.id}
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    {deletingId === holiday.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Holiday Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h2 className="text-xl font-bold text-gray-900">Add Holiday</h2>
                            <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        <form onSubmit={handleAdd} className="p-6 space-y-4">
                            <div>
                                <Label htmlFor="holiday-name">Holiday Name *</Label>
                                <Input
                                    id="holiday-name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g. Republic Day"
                                    className="mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="holiday-date">Date *</Label>
                                <Input
                                    id="holiday-date"
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                    className="mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="holiday-desc">Description (optional)</Label>
                                <Input
                                    id="holiday-desc"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="e.g. National holiday"
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} disabled={submitting}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                                    {submitting ? 'Adding...' : 'Add Holiday'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HolidayManagement;
